<template>
  <div
    ref="root"
    :tabindex="searchable ? -1 : tabindex"
    :class="rootClasses"
    role="combobox"
    :aria-owns="'listbox-' + id"
    @focus="activate()"
    @blur="searchable ? false : deactivate()"
    @keydown.self.down.prevent="pointerForward()"
    @keydown.self.up.prevent="pointerBackward()"
    @keypress.enter.tab.stop.self="addPointerElement($event)"
    @keyup.esc="deactivate()"
  >
    <input ref="selectHidden" type="hidden" />
    <slot name="caret" :toggle="toggle">
      <div
        class="ak-multiselect__select"
        @mousedown.prevent.stop="toggle()"
      ></div>
    </slot>
    <slot name="clear" :search="searchValue"></slot>
    <div ref="tags" class="ak-multiselect__tags" :class="inputErrorClass">
      <slot
        name="selection"
        :search="searchValue"
        :remove="removeElement"
        :values="visibleValues"
        :is-open="isOpen"
      >
        <div
          v-show="taggable && visibleValues.length > 0"
          class="ak-multiselect__tags-wrap"
          :class="{ 'ak-multiselect__tags-wrap--open': isOpen }"
        >
          <template v-for="(option, index) of visibleValues">
            <slot
              name="tag"
              :option="option"
              :search="searchValue"
              :remove="removeElement"
            >
              <span :key="index" class="ak-multiselect__tag">
                <span v-text="getOptionLabel(option)"></span>
                <span
                  tabindex="1"
                  class="ak-multiselect__tag-icon"
                  @keypress.enter.prevent="removeElement(option)"
                  @mousedown.prevent="removeElement(option)"
                >
                  <ak-icon type="close" />
                </span>
              </span>
            </slot>
          </template>
        </div>
        <div
          v-show="!taggable && visibleValues.length && !isOpen"
          class="ak-multiselect__single"
        >
          {{ visibleValues.length }} selected
        </div>
        <template v-if="internalValue && internalValue.length > limit">
          <slot name="limit">
            <strong
              class="ak-multiselect__strong"
              v-text="limitText(internalValue.length - limit)"
            />
          </slot>
        </template>
      </slot>
      <transition name="ak-multiselect-loading">
        <slot name="loading">
          <div v-show="loading" class="ak-multiselect__spinner">
            <ak-loading-spinner size="tiny" class="color-primary" />
          </div>
        </slot>
      </transition>
      <input
        v-if="searchable"
        :id="id"
        ref="search"
        :name="name"
        type="text"
        autocomplete="off"
        spellcheck="false"
        :placeholder="placeholder"
        :style="inputStyle"
        :value="searchValue"
        :disabled="disabled"
        :tabindex="tabindex"
        class="ak-multiselect__input"
        :aria-controls="'listbox-' + id"
        @input="updateSearch($event.target.value)"
        @focus.prevent="activate()"
        @blur.prevent="deactivate()"
        @keyup.esc="deactivate()"
        @keydown.down.prevent="pointerForward()"
        @keydown.up.prevent="pointerBackward()"
        @keypress.enter.prevent.stop.self="addPointerElement($event)"
        @keydown.delete.stop="removeLastElement()"
      />
      <span
        v-if="isSingleLabelVisible"
        ref="singleLabel"
        class="ak-multiselect__single"
        @mousedown.prevent="toggle"
      >
        <slot name="singleLabel" :option="singleValue">
          {{ currentOptionLabel }}
        </slot>
      </span>
      <span
        v-if="isPlaceholderVisible"
        class="ak-multiselect__placeholder"
        @mousedown.prevent="toggle"
      >
        <slot name="placeholder">
          {{ placeholder }}
        </slot>
      </span>
    </div>
    <transition name="ak-multiselect">
      <div
        v-show="isOpen"
        ref="list"
        class="ak-multiselect__content-wrapper"
        tabindex="-1"
        :style="{ maxHeight: optimizedHeight + 'px' }"
        @focus="activate"
        @mousedown.prevent
      >
        <ul
          :id="'listbox-' + id"
          class="ak-multiselect__content"
          :style="contentStyle"
          role="listbox"
        >
          <slot name="beforeList"></slot>
          <li v-if="multiple && max === internalValue.length">
            <span class="ak-multiselect__option">
              <slot name="maxElements"
                >Maximum of {{ max }} options selected. First remove a selected
                option to select another.</slot
              >
            </span>
          </li>
          <template v-if="!max || internalValue.length < max">
            <li
              v-for="(option, index) of filteredOptions"
              :id="id + '-' + index"
              :key="index"
              class="ak-multiselect__element"
              :role="
                !(option && (option.$isLabel || option.$isDisabled))
                  ? 'option'
                  : null
              "
            >
              <span
                v-if="!(option && (option.$isLabel || option.$isDisabled))"
                :class="optionHighlight(index, option)"
                :data-select="
                  option && option.isTag ? tagPlaceholder : selectLabelText
                "
                :data-selected="selectedLabelText"
                :data-deselect="deselectLabelText"
                class="ak-multiselect__option"
                @click.stop="select(option)"
                @mouseenter.self="pointerSet(index)"
              >
                <slot name="option" :option="option" :search="searchValue">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
              <span
                v-if="option && (option.$isLabel || option.$isDisabled)"
                :data-select="groupSelect && selectGroupLabelText"
                :data-deselect="groupSelect && deselectGroupLabelText"
                :class="groupHighlight(index, option)"
                class="ak-multiselect__option"
                @mouseenter.self="groupSelect && pointerSet(index)"
                @mousedown.prevent="selectGroup(option)"
              >
                <slot name="option" :option="option" :search="searchValue">
                  <span>{{ getOptionLabel(option) }}</span>
                </slot>
              </span>
            </li>
          </template>
          <li
            v-show="
              showNoResults &&
              filteredOptions.length === 0 &&
              searchValue &&
              !loading
            "
          >
            <span class="ak-multiselect__option">
              <slot name="noResult" :search="searchValue"
                >Nothing found. Try searching for something else.</slot
              >
            </span>
          </li>
          <li
            v-show="
              showNoOptions && options.length === 0 && !searchValue && !loading
            "
          >
            <span class="ak-multiselect__option">
              <slot name="noOptions">List is empty.</slot>
            </span>
          </li>
          <slot name="afterList"></slot>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue'

import { validationProps } from '../../mixins/formPropsMixin'
import validationMixin from '../../mixins/validationMixin'
import LoadingSpinner from '../loading/LoadingSpinner.vue'
import Icon from '../icon/Icon.vue'
import * as helpers from '../../utils/select'
import { hasOwn } from '../../utils/helpers'

/**
 * @type {String}
 * @description This is an error message that will be logged to the console if
 * the mac pop is passed if the multiple value is equal to false
 */
const maxMultiError =
  '[Select warn]: Max prop should not be used when prop Multiple equals false.'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-field :options="options" v-model="selected" />
   *
   * Based on:
   * https://github.com/shentao/vue-multiselect
   */
  name: 'AkSelect',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
    [LoadingSpinner.name]: LoadingSpinner,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...validationProps,

    /**
     * @default ''
     * @type {String}
     * @description name attribute to match optional label element
     */
    name: {
      type: String,
      default: undefined,
    },

    /**
     * @default 'Select'
     * @type {String}
     * @description String to show when pointing to an option
     */
    selectLabel: {
      type: String,
      default: 'Select',
    },

    /**
     * @default 'Select'
     * @type {String}
     * @description String to show when pointing to an option
     */
    selectGroupLabel: {
      type: String,
      default: 'Select group',
    },

    /**
     * @default 'Selected'
     * @type {String}
     * @description String to show next to selected option
     */
    selectedLabel: {
      type: String,
      default: 'Selected',
    },

    /**
     * @default 'Remove'
     * @type {String}
     * @description String to show when pointing to an already selected option
     */
    deselectLabel: {
      type: String,
      default: 'Remove',
    },

    /**
     * @default 'Remove'
     * @type {String}
     * @description String to show when pointing to an already selected option
     */
    deselectGroupLabel: {
      type: String,
      default: 'Remove group',
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Decide whether to show pointer labels
     */
    showLabels: {
      type: Boolean,
      default: true,
    },

    /**
     * @default 99999
     * @type {Integer}
     * @description Limit the display of selected options. The rest will
     * be hidden within the limitText string.
     */
    limit: {
      type: Number,
      default: 99999,
    },

    /**
     * @default 300
     * @type {Integer}
     * @description Sets maxHeight style value of the dropdown
     */
    maxHeight: {
      type: Number,
      default: 300,
    },

    /**
     * @default 'and * more'
     * @param {Int} count Number of elements more than limit
     * @type {Function}
     * @description Function that process the message shown when selected
     * elements pass the defined limit.
     */
    limitText: {
      type: Function,
      default: (count) => `and ${count} more`,
    },

    /**
     * @default False
     * @type {Boolean}
     * @description Set true to trigger the loading spinner.
     */
    loading: {
      type: Boolean,
      default: false,
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Disables the multiselect if true.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * @default ''
     * @type {String}
     * @description Fixed opening direction
     */
    openDirection: {
      type: String,
      default: undefined,
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Shows slot with message about empty options
     */
    showNoOptions: {
      type: Boolean,
      default: true,
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Shows no results markup
     */
    showNoResults: {
      type: Boolean,
      default: true,
    },

    /**
     * @default 0
     * @type {Boolean}
     * @description Sets the tab index of the select
     */
    tabindex: {
      type: Number,
      default: 0,
    },

    /**
     * Enable/disable highlighting of the pointed value.
     * @type {Boolean}
     * @default true
     */
    showPointer: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Boolean}
     * @description Decide whether to filter the results based on search query.
     * Useful for async filtering, where we search through more complex data.
     */
    internalSearch: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Array}
     * @description Array of available options: Objects, Strings or Integers.
     * If array of objects, visible label will default to option.label.
     * If `label` prop is passed, label will equal option['label']
     */
    options: {
      type: Array,
      required: true,
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Equivalent to the `multiple` attribute on a `<select>` input.
     */
    multiple: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Object||Array||String||Integer}
     * @description Presets the selected options value.
     */
    modelValue: {
      type: null,
      default() {
        return []
      },
    },

    /**
     * @default 'id'
     * @type {String}
     * @description Key to compare objects
     */
    trackBy: {
      type: String,
      default: undefined,
    },

    /**
     * @default 'label'
     * @type {String}
     * @description Label to look for in option Object
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Enable/disable search in options
     */
    searchable: {
      type: Boolean,
      default: true,
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Clear the search input after `)
     */
    clearOnSelect: {
      type: Boolean,
      default: true,
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Hide already selected options
     */
    hideSelected: {
      type: Boolean,
      default: false,
    },

    /**
     * @default 'Select option'
     * @type {String}
     * @description Equivalent to the `placeholder` attribute on a `<select>`
     * input.
     */
    placeholder: {
      type: String,
      default: 'Select option',
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Allow to remove all selected values
     */
    allowEmpty: {
      type: Boolean,
      default: true,
    },

    /**
     * @default false
     * @type {Boolean}
     * @desc Reset this.internalValue, this.search after this.internalValue
     * changes. Useful if want to create a stateless dropdown.
     */
    resetAfter: {
      type: Boolean,
      default: false,
    },

    /**
     * @default true
     * @type {Boolean}
     * @description Enable/disable closing after selecting an option
     */
    closeOnSelect: {
      type: Boolean,
      default: true,
    },

    /**
     * @default false
     * @type {Function}
     * @description Function to interpolate the custom label
     */
    customLabel: {
      type: Function,
      default(option, label) {
        if (helpers.isEmpty(option)) return ''
        return label ? option[label] : option
      },
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Disable / Enable tagging
     */
    taggable: {
      type: Boolean,
      default: false,
    },

    /**
     * @default 'Create a tag'
     * @type {String}
     * @description String to show when highlighting a potential tag
     */
    tagPlaceholder: {
      type: String,
      default: 'Create a tag',
    },

    /**
     * @default 'top'
     * @type {String}
     * @description By default new tags will appear above the search results.
     * Changing to 'bottom' will revert this behaviour and will prioritize
     * the search results
     */
    tagPosition: {
      type: String,
      default: 'top',
    },

    /**
     * @default 0
     * @type {Number}
     * @description Number of allowed selected options. No limit if 0.
     */
    max: {
      type: [Number, Boolean],
      default: false,
    },

    /**
     * @default null
     * @type {String|Integer}
     * @description Will be passed with all events as second param. Useful for
     *  identifying events origin.
     */
    id: {
      type: [String, Number],
      default: null,
    },

    /**
     * @default 1000
     * @type {Integer}
     * @descriptionLimits the options displayed in the dropdown to the first X
     * options.
     */
    optionsLimit: {
      type: Number,
      default: 1000,
    },

    /**
     * @default 1000
     * @type {String}
     * @description Name of the property containing the group values
     */
    groupValues: {
      type: String,
      default: undefined,
    },

    /**
     * @default 1000
     * @type {String}
     * @description Name of the property containing the group label
     */
    groupLabel: {
      type: String,
      default: undefined,
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Allow to select all group values by selecting the group label
     */
    groupSelect: {
      type: Boolean,
      default: false,
    },

    /**
     * @default 1000
     * @type {String}
     * @description Array of keyboard keys to block when selecting
     */
    blockKeys: {
      type: Array,
      default() {
        return []
      },
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Prevent from wiping up the search value
     */
    preserveSearch: {
      type: Boolean,
      default: false,
    },

    /**
     * @default false
     * @type {Boolean}
     * @description Select 1st options if value is empty
     */
    preselectFirst: {
      type: Boolean,
      default: false,
    },

    optionHeight: {
      type: Number,
      default: 40,
    },

    inlineEdit: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: [
    'update:modelValue',
    'open',
    'close',
    'select',
    'remove',
    'tag',
    'searchChange',
  ],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all
   * of the attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    // element refs
    const root = ref(null)
    const search = ref(null)
    const list = ref(null)

    const pointer = ref(0)
    const pointerDirty = ref(false)
    const searchValue = ref('')
    const isOpen = ref(false)
    const isValid = ref(true)
    const preferredOpenDirection = ref('below')
    const optimizedHeight = ref(props.maxHeight)
    const pointerPosition = computed(() => pointer.value * props.optionHeight)
    const visibleElements = computed(
      () => optimizedHeight.value / props.optionHeight
    )

    /**
     * @returns {Mixed}
     * @description Returns the internal value of the select component
     */
    const internalValue = computed(() => {
      return props.modelValue || props.modelValue === 0
        ? Array.isArray(props.modelValue)
          ? props.modelValue
          : [props.modelValue]
        : []
    })

    /**
     * @returns {Array}
     * @description Returns a list of the filtered options groups for the
     * select component
     */
    const filteredOptions = computed(() => {
      const search = searchValue.value || ''
      const normalizedSearch = search.toLowerCase().trim()
      let options = props.options.concat()

      /* istanbul ignore else */
      if (props.internalSearch) {
        options = props.groupValues
          ? filterAndFlat(options, normalizedSearch, props.label)
          : helpers.filterOptions(
              options,
              normalizedSearch,
              props.label,
              props.customLabel
            )
      } else {
        options = props.groupValues
          ? helpers.flattenOptions(props.groupValues, props.groupLabel)(options)
          : options
      }

      options = props.hideSelected
        ? options.filter(helpers.not(isSelected))
        : options

      /* istanbul ignore else */
      if (
        props.taggable &&
        normalizedSearch.length &&
        !isExistingOption(normalizedSearch)
      ) {
        if (props.tagPosition === 'bottom') {
          options.push({ isTag: true, label: search })
        } else {
          options.unshift({ isTag: true, label: search })
        }
      }

      return options.slice(0, props.optionsLimit)
    })

    /**
     * @returns {Mixed}
     * @description Returns the keys for the values in the select component
     * these are used in the for loop to be able to track each of the select
     * option values.
     */
    const valueKeys = computed(() => {
      if (props.trackBy) {
        return internalValue.value.map((element) => element[props.trackBy])
      } else {
        return internalValue.value
      }
    })

    /**
     * @returns {Mixed}
     * @description Returns the keys for the options in the select component
     * these are used in the for loop to be able to track each of the select
     * options.
     */
    const optionKeys = computed(() => {
      const options = props.groupValues
        ? flatAndStrip(props.options)
        : props.options
      return options.map((element) =>
        props.customLabel(element, props.label).toString().toLowerCase()
      )
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean flag to determine if the place holder
     * text should be visable or not
     */
    const isPlaceholderVisible = computed(() => {
      return !internalValue.value.length && (!props.searchable || !isOpen.value)
    })

    /**
     * @returns {Array}
     * @description Reutns an array of the visable values
     */
    const visibleValues = computed(() => {
      return props.multiple ? internalValue.value.slice(0, props.limit) : []
    })

    /**
     * @returns {Mixed}
     * @description Returns the first value
     */
    const singleValue = computed(() => {
      return internalValue.value[0]
    })

    /**
     * @returns {String}
     * @description Returns the label for the current option.
     */
    const currentOptionLabel = computed(() => {
      return props.multiple
        ? props.searchable
          ? ''
          : props.placeholder
        : internalValue.value.length
        ? getOptionLabel(internalValue.value[0])
        : props.searchable
        ? ''
        : props.placeholder
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean flag to determine if the single label
     * should be displaed or not
     */
    const isSingleLabelVisible = computed(() => {
      return (
        (singleValue.value || singleValue.value === 0) &&
        (!isOpen.value || !props.searchable) &&
        !visibleValues.value.length
      )
    })

    /**
     * @returns {String}
     * @description Returns the deselect label text
     */
    const deselectLabelText = computed(() => {
      return props.showLabels ? props.deselectLabel : ''
    })

    /**
     * @returns {String}
     * @description Returns the deselect group label text
     */
    const deselectGroupLabelText = computed(() => {
      return props.showLabels ? props.deselectGroupLabel : ''
    })

    /**
     * @returns {String}
     * @description Returns the select label text
     */
    const selectLabelText = computed(() => {
      return props.showLabels ? props.selectLabel : ''
    })

    /**
     * @returns {String}
     * @description Returns the select group label text
     */
    const selectGroupLabelText = computed(() => {
      return props.showLabels ? props.selectGroupLabel : ''
    })

    /**
     * @returns {String}
     * @description Returns the selected label text
     */
    const selectedLabelText = computed(() => {
      return props.showLabels ? props.selectedLabel : ''
    })

    /**
     * @returns {Object}
     * @description Returns the list of classes for the root element of the
     * component
     */
    const rootClasses = computed(() => ({
      'ak-multiselect--active': isOpen.value,
      'ak-multiselect--disabled': props.disabled,
      'ak-multiselect--above': isAbove.value,
      'ak-select': !props.multiple,
      'ak-multiselect': props.multiple,
      'ak-select--inline-edit': props.inlineEdit,
    }))

    /**
     * @returns {Object|Null}
     * @description Returns the styles for the input element
     */
    const inputStyle = computed(() => {
      if (
        props.searchable ||
        (props.multiple && props.modelValue && props.modelValue.length)
      ) {
        // Hide input by setting the width to 0 allowing it to
        // receive focus
        return isOpen.value
          ? { width: '100%' }
          : { width: '0', position: 'absolute', padding: '0' }
      }
      return null
    })

    /**
     * @returns {Object}
     * @description Returns a object to configure the content style of the
     * markup in the options.
     */
    const contentStyle = computed(() => {
      return props.options.length
        ? { display: 'inline-block' }
        : { display: 'block' }
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean flag to determine if the dropdown is
     * above of below the input.
     */
    const isAbove = computed(() => {
      if (props.openDirection === 'above' || props.openDirection === 'top') {
        return true
      } else if (
        props.openDirection === 'below' ||
        props.openDirection === 'bottom'
      ) {
        return false
      } else {
        return preferredOpenDirection.value === 'above'
      }
    })

    /**
     * @returns {Boolean}
     * @description Returns a boolean flag to determine if the search
     * input should be shown or not.
     */
    const showSearchInput = computed(() => {
      return (
        props.searchable &&
        (props.hasSingleSelectedSlot &&
        (props.visibleSingleValue || props.visibleSingleValue === 0)
          ? isOpen.value
          : true)
      )
    })

    /**
     * @param  {Object||String||Integer} option passed element to check
     * @returns {Boolean} returns true if element is selected
     * @description Finds out if the given element is already present in the
     * result value
     */
    const isSelected = (option) => {
      const opt = props.trackBy ? option[props.trackBy] : option
      return valueKeys.value.indexOf(opt) > -1
    }

    /**
     * @param  {String}
     * @returns {Boolean} returns true if element is available
     * @description Finds out if the given query is already present in the available
     * options
     */
    const isExistingOption = (query) => {
      return !props.options ? false : optionKeys.value.indexOf(query) > -1
    }

    /**
     * @param  {Array}
     * @returns {Array} returns a flat options list without group labels
     * @description Flattens and then strips the group labels from the options list
     */
    const flatAndStrip = (options) => {
      return helpers.flow(
        helpers.flattenOptions(props.groupValues, props.groupLabel),
        helpers.stripGroups
      )(options)
    }

    /**
     * @param  {Array}
     * @returns {Array} returns a filtered and flat options list
     * @description Filters and then flattens the options list
     */
    const filterAndFlat = (options, search, label) => {
      return helpers.flow(
        helpers.filterGroups(
          search,
          label,
          props.groupValues,
          props.groupLabel,
          props.customLabel
        ),
        helpers.flattenOptions(props.groupValues, props.groupLabel)
      )(options)
    }

    /**
     * @param  {Object||String||Integer} Passed option
     * @returns {Object||String}
     * @description Returns empty string when options is null/undefined. Returns
     * tag query if option is tag. Returns the customLabel() results and casts
     * it to string.
     */
    const getOptionLabel = (option) => {
      if (helpers.isEmpty(option)) return ''
      /* istanbul ignore else */
      if (option.isTag) return option.label
      /* istanbul ignore else */
      if (option.$isLabel) return option.$groupLabel

      let label = props.customLabel(option, props.label)
      /* istanbul ignore else */
      if (helpers.isEmpty(label)) return ''
      return label
    }

    /**
     * @param  {String}
     * @description Updates the search value
     */
    const updateSearch = (query) => {
      searchValue.value = query
    }

    /**
     * @returns {Object||Array||String||Integer}
     * @description Returns the internalValue in a way it can be emitted to
     * the parent
     */
    const getValue = () => {
      return props.multiple
        ? internalValue.value
        : internalValue.value.length === 0
        ? null
        : internalValue.value[0]
    }

    /**
     * @description Updates the hasEnoughSpace variable used for detecting
     * where to expand the dropdown
     */
    const adjustPosition = () => {
      if (typeof window === 'undefined') return

      const spaceAbove = root.value.getBoundingClientRect().top
      const spaceBelow =
        window.innerHeight - root.value.getBoundingClientRect().bottom
      const hasEnoughSpaceBelow = spaceBelow > props.maxHeight

      if (
        hasEnoughSpaceBelow ||
        spaceBelow > spaceAbove ||
        props.openDirection === 'below' ||
        props.openDirection === 'bottom'
      ) {
        preferredOpenDirection.value = 'below'
        optimizedHeight.value = Math.min(spaceBelow - 40, props.maxHeight)
      } else {
        preferredOpenDirection.value = 'above'
        optimizedHeight.value = Math.min(spaceAbove - 40, props.maxHeight)
      }
    }

    /**
     * @description Opens the selects dropdown. Sets this.isOpen to TRUE
     */
    const activate = () => {
      /* istanbul ignore else */
      if (isOpen.value || props.disabled) return

      adjustPosition()

      /* istanbul ignore else  */
      if (
        props.groupValues &&
        pointer.value === 0 &&
        filteredOptions.value.length
      ) {
        pointer.value = 1
      }

      isOpen.value = true

      /* istanbul ignore else  */
      if (props.searchable) {
        if (!props.preserveSearch) searchValue.value = ''
        nextTick(() => search.value && search.value.focus())
      } else {
        root.value.focus()
      }

      emit('open', props.id)
    }

    /**
     * @description Closes the multiselect’s dropdown. Sets this.isOpen to FALSE
     */
    const deactivate = () => {
      /* istanbul ignore else */
      if (!isOpen.value) return

      isOpen.value = false

      /* istanbul ignore else  */
      if (props.searchable) {
        search.value && search.value.blur()
      } else {
        root.value.blur()
      }

      if (!props.preserveSearch) {
        searchValue.value = ''
      }

      emit('close', getValue(), props.id)
    }

    /**
     * @description Call this.activate() or this.deactivate() depending on
     * this.isOpen value.
     *
     * @fires this#activate || this#deactivate
     * @property {Boolean} isOpen indicates if dropdown is open
     */
    const toggle = () => {
      isOpen.value ? deactivate() : activate()
    }

    /**
     * @param  {Object||String||Integer} option passed element to check
     * @returns {Boolean} returns true if element is disabled
     * @description Finds out if the given option is disabled
     */
    const isOptionDisabled = (option) => {
      return !!option.$isDisabled
    }

    /**
     * @description Removes the given option from the selected options. Additionally
     * checks this.allowEmpty prop if option can be removed when it is the last
     * selected option.
     *
     * @param  {type} option description
     * @returns {type}        description
     */
    const removeElement = (option, shouldClose = true) => {
      /* istanbul ignore else */
      if (props.disabled) return

      /* istanbul ignore else */
      if (option.$isDisabled) return

      /* istanbul ignore else */
      if (!props.allowEmpty && internalValue.value.length <= 1) {
        deactivate()
        return
      }

      const index =
        typeof option === 'object'
          ? valueKeys.value.indexOf(option[props.trackBy])
          : valueKeys.value.indexOf(option)

      emit('remove', option, props.id)

      if (props.multiple) {
        const newValue = internalValue.value
          .slice(0, index)
          .concat(internalValue.value.slice(index + 1))

        emit('update:modelValue', newValue, props.id)
      } else {
        emit('update:modelValue', null, props.id)
      }

      /* istanbul ignore else */
      if (props.closeOnSelect && shouldClose) deactivate()
    }

    /**
     * @description Calls this.removeElement() with the last element from
     * this.internalValue (selected element Array)
     *
     * @fires this#removeElement
     */
    const removeLastElement = () => {
      /* istanbul ignore else */
      if (props.blockKeys.indexOf('Delete') !== -1) return

      /* istanbul ignore else */
      if (
        searchValue.value.length === 0 &&
        Array.isArray(internalValue.value) &&
        internalValue.value.length
      ) {
        removeElement(
          internalValue.value[internalValue.value.length - 1],
          false
        )
      }
    }

    /**
     * @param  {Object||String||Integer} option to select/deselect
     * @param  {Boolean} block removing
     * @description Add the given option to the list of selected options or
     * sets the option as the selected option. If option is already
     * selected -> remove it from the results.
     */
    const select = (option, key) => {
      /* istanbul ignore else */
      if (option.$isLabel && props.groupSelect) {
        selectGroup(option)
        return
      }

      if (
        props.blockKeys.indexOf(key) !== -1 ||
        props.disabled ||
        option.$isDisabled ||
        option.$isLabel
      ) {
        return
      }

      /* istanbul ignore else */
      if (
        props.max &&
        props.multiple &&
        internalValue.value.length === props.max
      ) {
        return
      }

      /* istanbul ignore else */
      if (key === 'Tab' && !pointerDirty.value) return

      if (option.isTag) {
        emit('tag', option.label, props.id)
        searchValue.value = ''
        if (props.closeOnSelect && !props.multiple) deactivate()
      } else {
        if (isSelected(option)) {
          if (key !== 'Tab') removeElement(option)
          return
        }

        emit('select', option, props.id)

        if (props.multiple) {
          emit(
            'update:modelValue',
            internalValue.value.concat([option]),
            props.id
          )
        } else {
          emit('update:modelValue', option, props.id)
        }

        /* istanbul ignore else */
        if (props.clearOnSelect) searchValue.value = ''
      }

      /* istanbul ignore else */
      if (props.closeOnSelect) deactivate()
    }

    /**
     * @param  {Object||String||Integer} group to select/deselect
     * @description Add the given group options to the list of selected options
     * If all group optional are already selected -> remove it from the results.
     */
    const selectGroup = (selectedGroup) => {
      const group = props.options.find((option) => {
        return option[props.groupLabel] === selectedGroup.$groupLabel
      })

      if (!group) return

      if (wholeGroupSelected(group)) {
        emit('remove', group[props.groupValues], props.id)

        const newValue = internalValue.value.filter(
          (option) => group[props.groupValues].indexOf(option) === -1
        )

        emit('update:modelValue', newValue, props.id)
      } else {
        const optionsToAdd = group[props.groupValues].filter(
          (option) => !(isOptionDisabled(option) || isSelected(option))
        )

        emit('select', optionsToAdd, props.id)
        emit(
          'update:modelValue',
          internalValue.value.concat(optionsToAdd),
          props.id
        )
      }
    }

    /**
     * @param {Object} group to validated selected values against
     * @description Helper to identify if all values in a group are selected
     */
    const wholeGroupSelected = (group) => {
      return group[props.groupValues].every(
        (option) => isSelected(option) || isOptionDisabled(option)
      )
    }

    /**
     * @param {Object} group to check for disabled values
     * @description Helper to identify if all values in a group are disabled
     */
    const wholeGroupDisabled = (group) => {
      return group[props.groupValues].every(isOptionDisabled)
    }

    /**
     * @param {Number} index
     * @param {Object} option
     * @description Returns the classes for an option to set if highlighted
     * or not
     */
    const optionHighlight = (index, option) => {
      return {
        'ak-multiselect__option--highlight':
          index === pointer.value && props.showPointer,
        'ak-multiselect__option--selected': isSelected(option),
      }
    }

    /**
     * @type {Function}
     * @description Returns the classes for the groups
     */
    const groupHighlight = (index, selectedGroup) => {
      if (!props.groupSelect) {
        return [
          'ak-multiselect__option--group',
          'ak-multiselect__option--disabled',
        ]
      }

      const group = props.options.find((option) => {
        return option[props.groupLabel] === selectedGroup.$groupLabel
      })

      return group && !wholeGroupDisabled(group)
        ? [
            'ak-multiselect__option--group',
            {
              'ak-multiselect__option--highlight':
                index === pointer.value && props.showPointer,
            },
            {
              'ak-multiselect__option--group-selected':
                wholeGroupSelected(group),
            },
          ]
        : 'ak-multiselect__option--disabled'
    }

    /**
     * @type {Function}
     * @description Selects an option based on the pointer value
     */
    const addPointerElement = ({ key } = 'Enter') => {
      /* istanbul ignore else */
      if (filteredOptions.value.length > 0) {
        select(filteredOptions.value[pointer.value], key)
      }
      pointerReset()
    }

    /**
     * @type {Function}
     * @description Moves the pointer backwards
     */
    const pointerBackward = () => {
      if (pointer.value > 0) {
        pointer.value--
        /* istanbul ignore else */
        if (list.value.scrollTop >= pointerPosition.value) {
          list.value.scrollTop = pointerPosition.value
        }
        /* istanbul ignore else */
        if (
          filteredOptions.value[pointer.value] &&
          filteredOptions.value[pointer.value].$isLabel &&
          !props.groupSelect
        )
          pointerBackward()
      } else {
        /* istanbul ignore else */
        if (
          filteredOptions.value[pointer.value] &&
          filteredOptions.value[0].$isLabel &&
          !props.groupSelect
        )
          pointerForward()
      }
      pointerDirty.value = true
    }

    /**
     * @type {Function}
     * @description Moves the pointer forwards
     */
    const pointerForward = () => {
      /* istanbul ignore else */
      if (pointer.value < filteredOptions.value.length - 1) {
        pointer.value++
        /* istanbul ignore next */
        if (
          list.value.scrollTop <=
          pointerPosition.value -
            (visibleElements.value - 1) * props.optionHeight
        ) {
          list.value.scrollTop =
            pointerPosition.value -
            (visibleElements.value - 1) * props.optionHeight
        }
        /* istanbul ignore else */
        if (
          filteredOptions.value[pointer.value] &&
          filteredOptions.value[pointer.value].$isLabel &&
          !props.groupSelect
        ) {
          pointerForward()
        }
      }
      pointerDirty.value = true
    }

    /**
     * @description Moves the pointer
     */
    const pointerAdjust = () => {
      /* istanbul ignore else */
      if (pointer.value >= filteredOptions.value.length - 1) {
        pointer.value = filteredOptions.value.length
          ? filteredOptions.value.length - 1
          : 0
      }

      if (
        filteredOptions.value.length > 0 &&
        filteredOptions.value[pointer.value].$isLabel &&
        !props.groupSelect
      ) {
        pointerForward()
      }
    }

    /**
     * @param {Number} index
     * @description Sets the pointer value
     */
    const pointerSet = (index) => {
      pointer.value = index
      pointerDirty.value = true
    }

    /**
     * @description Resets the pointer value
     */
    const pointerReset = () => {
      /* istanbul ignore else */
      if (!props.closeOnSelect) return
      pointer.value = 0
      /* istanbul ignore else */
      if (list.value) {
        list.value.scrollTop = 0
      }
    }

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: 'select',
      emit,
      props,
      attrs,
      input: ref({
        validationMessage:
          props.validationMessage ||
          'Please choose an option if you want to proceed.',
        customRuleValidationMessage: '',
        setCustomValidity(message) {
          this.customRuleValidationMessage = message
          if (message.length > 0) isValid.value = false
          else isValid.value = true
        },
        checkValidity() {
          const required = hasOwn(attrs, 'required')
          const hasCustomRule =
            hasOwn(props, 'customRules') && props.customRules.length > 0

          if (hasCustomRule && !isValid.value) {
            this.validationMessage =
              this.customRuleValidationMessage.length > 0
                ? this.customRuleValidationMessage
                : 'Please choose an option if you want to proceed.'
            return false
          }

          if (required && internalValue.value.length === 0) {
            isValid.value = false
            return false
          } else {
            isValid.value = true
            return true
          }
        },
      }),
      value: internalValue,
    })

    watch(internalValue, () => {
      /* istanbul ignore else */
      if (props.resetAfter && internalValue.value.length) {
        searchValue.value = ''
        emit('update:modelValue', props.multiple ? [] : null)
      }
      // Here we are going to trigger the validation as the modal will have changed
      // this validation will trigger all of the needed methods in the parent
      //ak-field and ak-form components.
      validation.isPristine.value = false
      validation.validate()
    })

    watch(searchValue, () => {
      emit('searchChange', searchValue.value, props.id)
    })

    watch(filteredOptions, () => {
      pointerAdjust()
    })

    watch(isOpen, () => {
      pointerDirty.value = false
    })

    onMounted(() => {
      /* istanbul ignore else */
      if (!props.multiple && props.max) {
        console.warn(maxMultiError)
      }
      if (
        props.preselectFirst &&
        !internalValue.value.length &&
        props.options.length
      ) {
        select(filteredOptions.value[0])
      }
      validation.validate()
    })

    onBeforeUnmount(() => {
      validation.beforeUnmount()
    })

    return {
      ...validation,
      root,
      search,
      list,
      pointer,
      pointerDirty,
      searchValue,
      isOpen,
      isValid,
      preferredOpenDirection,
      optimizedHeight,
      pointerPosition,
      visibleElements,
      internalValue,
      filteredOptions,
      optionKeys,
      valueKeys,
      isPlaceholderVisible,
      visibleValues,
      singleValue,
      isSelected,
      isExistingOption,
      flatAndStrip,
      currentOptionLabel,
      getOptionLabel,
      updateSearch,
      getValue,
      toggle,
      deactivate,
      activate,
      adjustPosition,
      isOptionDisabled,
      removeElement,
      removeLastElement,
      select,
      selectGroup,
      wholeGroupDisabled,
      wholeGroupSelected,
      isSingleLabelVisible,
      deselectLabelText,
      deselectGroupLabelText,
      selectLabelText,
      selectGroupLabelText,
      selectedLabelText,
      inputStyle,
      contentStyle,
      isAbove,
      showSearchInput,
      pointerSet,
      pointerReset,
      pointerAdjust,
      pointerForward,
      pointerBackward,
      addPointerElement,
      optionHighlight,
      groupHighlight,
      rootClasses,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
fieldset[disabled] .ak-multiselect,
fieldset[disabled] .ak-select {
  pointer-events: none;
}

.ak-multiselect,
.ak-select {
  $self: &;
  display: block;
  position: relative;
  width: 100%;
  text-align: left;
  color: $color-text;

  &:focus {
    outline: none;
  }

  &,
  &__input,
  &__single {
    touch-action: manipulation;
  }

  &__input {
    border: 0;
    font-family: $base-font-family;
    font-size: $base-font-size;
    &::placeholder {
      color: $color-text;
    }
  }

  &__input,
  &__single {
    position: relative;
    display: inline-block;
    border: none;
    background: #fff;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    &:focus {
      outline: none;
    }
  }

  &__select {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    z-index: 1;
    width: $input-total-height;
    height: 100%;
    right: 1px;
    top: 1px;
    padding: 4px 8px;
    margin: 0;
    text-decoration: none;
    text-align: center;
    cursor: pointer;
    transition: transform 0.2s ease;

    &::before {
      color: $color-text;
      border-style: solid;
      border-width: 5px 5px 0 5px;
      border-color: $color-text transparent transparent transparent;
      content: '';
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    background-color: #fff;
    border: 1px solid $border-input-color;
    border-radius: $base-border-radius;
    box-shadow: $form-box-shadow;
    box-sizing: border-box;
    font-family: $base-font-family;
    font-size: $base-font-size;
    width: 100%;
    padding: calc($spacing / 3) $input-total-height calc($spacing / 3)
      $spacing-small;
    min-height: $input-total-height;
    border-radius: $base-border-radius;
    transition-property: border-color, box-shadow, background-color, color;
    transition-duration: $transition-time;
  }

  &__tags-wrap {
    display: inline-block;
    margin-top: -$spacing-tiny;

    &--open {
      margin-bottom: $spacing-tiny;
    }
  }

  &__tag {
    position: relative;
    display: inline-block;
    padding: 0.25em 2em 0.25em 0.825em;
    margin-right: $spacing-tiny;
    margin-top: $spacing-tiny;
    margin-bottom: -$spacing-tiny;
    border-radius: $base-border-radius;
    color: #fff;
    background: $color-primary;
    font-size: 0.825em;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    border: 1px solid transparent;

    &-icon {
      border-radius: 50%;
      position: absolute;
      top: 0.35em;
      right: 0.25em;
      width: 1.5em;
      height: 1.5em;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      &:hover,
      &:focus {
        background-color: rgba(255, 255, 255, 0.15);
      }
    }

    ~ #{$self}__input,
    ~ #{$self}__single {
      width: auto;
    }
  }

  &__placeholder {
    color: $color-grey-light;
    display: inline-block;
    margin-left: 2px; //fix for caret

    #{$self}--active & {
      display: none;
    }
  }

  &__strong {
    display: inline-block;
    font-size: 0.825em;
    margin-top: calc($spacing-tiny / 2);
    margin-bottom: calc($spacing-tiny / 2);
  }

  &__current {
    display: block;
    overflow: hidden;
    padding: 8px 12px 0;
    padding-right: $input-total-height;
    white-space: nowrap;
    margin: 0;
    text-decoration: none;
    cursor: pointer;
  }

  &__content-wrapper {
    position: absolute;
    display: block;
    background: #fff;
    width: 100%;
    max-height: 240px;
    overflow: auto;
    border: 1px solid $border-input-color;
    border-top: none;
    border-bottom-left-radius: $base-border-radius;
    border-bottom-right-radius: $base-border-radius;
    z-index: 50;
    -webkit-overflow-scrolling: touch;

    &.ak-multiselect--above {
      bottom: 100%;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border-top-left-radius: $base-border-radius;
      border-top-right-radius: $base-border-radius;
      border-bottom: none;
      border-top: 1px solid $border-input-color;
    }
  }

  &__content {
    list-style: none;
    display: inline-block;
    padding: 0;
    margin: 0;
    min-width: 100%;
    vertical-align: top;
  }

  &__element {
    display: block;
  }

  &__option {
    display: block;
    padding: $spacing-tiny $spacing-small;
    text-decoration: none;
    text-transform: none;
    position: relative;
    cursor: pointer;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
    &::after {
      top: $spacing-tiny;
      right: 0;
      position: absolute;
      padding-right: $spacing-small;
      padding-left: $spacing-small;
      font-size: 13px;
    }

    &--highlight {
      background: rgba($color-primary, 0.15);
      outline: none;
      color: black;
      &::after {
        content: attr(data-select);
        color: rgba($color-primary, 0.6);
      }
    }

    &--selected {
      // background: $color-primary-light;
      color: $color-primary;
      font-weight: bold;
      &::after {
        content: attr(data-selected);
        color: $color-primary;
        font-weight: normal;
      }
    }

    &--disabled #{$self}__current,
    &--disabled #{$self}__select {
      background: $color-grey-lightest;
      color: $color-grey-light;
    }

    &--disabled {
      background: $color-grey-lightest !important;
      color: $color-grey-light !important;
      cursor: text;
      pointer-events: none;
    }

    &--group {
      background: $color-grey-lightest;
      color: $color-grey-dark;

      &#{$self}__option--highlight {
        background-color: $color-grey;
        color: #fff;
        font-weight: bold;

        &::after {
          color: #fff;
          font-weight: normal;
        }
      }
    }
  }

  // Modifiers
  &--disabled {
    background-color: $color-grey-lightest;
    pointer-events: none;
    color: rgba($color-text, 0.625);
    border-color: lighten($border-input-color, 10%);
  }

  &--active {
    z-index: 50;

    #{$self}__select {
      transform: rotateZ(180deg);
    }

    #{$self}__tags {
      border-color: $color-action;
      box-shadow: $form-box-shadow-focus;
    }

    &:not(#{$self}--above) {
      #{$self}__current,
      #{$self}__input,
      #{$self}__tags {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }

  &--above {
    &:not(#{$self}--above) {
      #{$self}__current,
      #{$self}__input,
      #{$self}__tags {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }
}

.ak-select--inline-edit {
  cursor: pointer;
  .ak-multiselect__input,
  .ak-multiselect__single {
    padding-top: 0.275rem;
    padding-bottom: 0.275rem;
    min-height: threequarts($input-total-height);
    background-color: transparent;
    width: 80%;
  }
  .ak-multiselect__tags {
    padding-top: $spacing-tiny;
    padding-bottom: $spacing-tiny;
    padding: 0;
    height: auto;
    min-height: 2em;
    border: 0;
    font-size: inherit;
    box-shadow: none !important;
    background-color: transparent;
    &:focus,
    &:active,
    &:hover {
      background-color: lighten($color-action, 37.5%);
      color: #000;
    }
  }
}

// Remove only on multiselect
.ak-multiselect {
  .ak-multiselect__option--selected {
    &.ak-multiselect__option--highlight {
      background: rgba($color-danger, 0.15);
      color: $color-grey-dark;
      &::after {
        content: attr(data-deselect);
        color: $color-danger;
      }
    }
  }
  .ak-multiselect__option--group-selected {
    &.ak-multiselect__option--highlight {
      background-color: $color-danger;
      color: #fff;

      &::after {
        color: #fff;
      }
    }
  }
}

.ak-multiselect-enter-active,
.ak-multiselect-leave-active {
  transition: all 0.15s ease;
}

.ak-multiselect-enter,
.ak-multiselect-leave-active {
  opacity: 0;
}

.ak-multiselect-loading-enter-active,
.ak-multiselect-loading-leave-active {
  transition: opacity 0.4s ease-in-out;
  opacity: 1;
}

.ak-multiselect-loading-enter,
.ak-multiselect-loading-leave-active {
  opacity: 0;
}
</style>
